import { OpenAIApi } from "api/openAIApi";
import { Service, ServiceDescription } from "types/entities";
import { languages } from "types/internationalization";

// Function to save the services into local storage
export const setServicesInLocalStorage = (services: Service[]) => {
	// Mapping services into ServiceDescription[]
	const mappedServicesEsp = services.map((s) => {
		return {
			id: s.id,
			name: s.name,
			shortDescription: s.shortDescription,
			description: s.description,
			price: s.price,
			image: s.image,
		};
	});

	let storedServices: Record<string, ServiceDescription[]>;

	// Getting services saved in the local storage
	let storedServicesStr = localStorage.getItem("services");
	let servicesChanged = false;
	if (storedServicesStr) {
		storedServices = JSON.parse(storedServicesStr);
		// Getting stored services in spanish
		const storedServicesEsp = storedServices["es-ES"];
		// Checking if the stored services and the received ones have changed
		servicesChanged =
			JSON.stringify(storedServicesEsp) !== JSON.stringify(mappedServicesEsp);
	} else {
		storedServices = {};
	}

	// Getting mustTranslate item from local storage
	let mustTranslateStr = localStorage.getItem("mustTranslate");
	let mustTranslate: Record<string, boolean>;
	if (mustTranslateStr) mustTranslate = JSON.parse(mustTranslateStr);
	else mustTranslate = {};

	// If services have changed, setting the mustTranslate into true in all languages except spanish
	languages.forEach((language) => {
		if (language === "es-ES") return;
		if (servicesChanged || !storedServices[language]) {
			mustTranslate[language] = true;
		}
	});
	localStorage.setItem("mustTranslate", JSON.stringify(mustTranslate));

	// Setting stored services in spanish
	storedServices["es-ES"] = mappedServicesEsp;
	localStorage.setItem("services", JSON.stringify(storedServices));
};

// This function checks the mustTranslate and service in localStorage and generates
// the translations if necessary
export const generateTranslations = async (
	mappedServices: ServiceDescription[],
	locale: string,
	setLoadingTranslations: Function,
) => {
	let translatedServices;

	// Getting mustTranslate to know if the services are already translated in the selected language
	const mustTranslateStr = localStorage.getItem("mustTranslate");
	let mustTranslate: Record<string, boolean>;

	if (mustTranslateStr) mustTranslate = JSON.parse(mustTranslateStr);
	else mustTranslate = {};

	// Getting stored services and setting the service into spanish if it's empty
	const storedServicesStr = localStorage.getItem("services");
	let storedServices: Record<string, ServiceDescription[]>;

	if (!storedServicesStr) storedServices = { "es-ES": mappedServices };
	else storedServices = JSON.parse(storedServicesStr);

	// If mustTranslate is empty or is true in the selected language, then translate them
	if (!mustTranslateStr || mustTranslate[locale]) {
		setLoadingTranslations(true);
		const shortServices = mappedServices.map((s) => {
			return { ...s, image: undefined, price: undefined };
		});

		try {
			const openAIApi = new OpenAIApi();
			const response = await openAIApi.getOpenAITranslations(
				`¿Me traducis al ${locale} los atributos de los objetos del arreglo que te voy a enviar? Te voy a mandar un json del formato: [{id: string, name: string, description: string, shortDescription: string, price: number, image: string}], necesito que me devuelvas el mismo json que te envié pero traducido y manteniendo las etiquetas HTML. No me mandes ninguna explicación, solo el json: ${JSON.stringify(
					shortServices
				)}`
			);

			// Getting the results generated by the IA, deleting the 'json' tags and setting
			// the corresponded image and price
			const translations = JSON.parse(
				response.replace(/```json/g, "").replace(/```/g, "")
			).map((s: ServiceDescription) => {
				const service = mappedServices.find((service) => service.id === s.id);
				return {
					...s,
					image: service!.image,
					price: service!.price,
				};
			});

			translatedServices = translations;

			// Saving the translations in the localStorage
			const updatedServices = storedServices;
			updatedServices[locale] = translations;
			localStorage.setItem("services", JSON.stringify(updatedServices));

			// Setting to false the mustTranslate in the selected language
			if (!mustTranslateStr)
				languages.forEach((language) => {
					if (language === "es-ES") return;
					if (!storedServices[language]) {
						mustTranslate[language] = true;
					}
				});

			mustTranslate[locale] = false;
			localStorage.setItem("mustTranslate", JSON.stringify(mustTranslate));
		} catch (error) {
			console.error("Error during translation request:", error);
		} finally {
			setLoadingTranslations(false);
		}
	} else {
		translatedServices = storedServices[locale];
	}
	
	return translatedServices;
};
